import { useEffect, useRef } from 'react';

/**
 * Fullscreen bootstrap (SCN-612). The Fullscreen API only grants a request that follows a user
 * gesture, so we arm one-shot listeners for the first click / keypress / touch. `requestNow()` is
 * also called right after pairing completes — if that isn't gesture-adjacent the browser rejects it
 * silently, which is fine (the gesture listeners are still armed).
 */
export function useFullscreen() {
  const doneRef = useRef(false);

  const requestNow = () => {
    if (doneRef.current || document.fullscreenElement) return;
    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    };
    const req = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
    if (!req) return;
    Promise.resolve(req())
      .then(() => {
        doneRef.current = true;
      })
      .catch(() => {
        /* not permitted without a gesture — the listeners below will retry */
      });
  };

  useEffect(() => {
    const onGesture = () => {
      requestNow();
      if (doneRef.current) detach();
    };
    const detach = () => {
      window.removeEventListener('click', onGesture);
      window.removeEventListener('keydown', onGesture);
      window.removeEventListener('touchend', onGesture);
    };
    window.addEventListener('click', onGesture);
    window.addEventListener('keydown', onGesture);
    window.addEventListener('touchend', onGesture);
    return detach;
  }, []);

  return { requestNow };
}
